import { Component, input, output, inject, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { DropdownModule } from 'primeng/dropdown';
import {
  Profesional,
  FiltroProfesional
} from 'src/app/domain/model/profesional/profesional';
import { ProfesionalRepository } from 'src/app/domain/service/profesional/profesional-repository';
import {
  sanitizeText,
  sanitizeTextPreserveSpaces
} from 'src/app/common/input-utils';
import { HttpProfesionalRepository } from 'src/app/infrastructure/adapters/profesional/http-profesional.repository';
import { busquedaProfesionalPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { BaseBusquedaPaginadaDialog } from 'src/app/presentation/shared/busquedas/base-busqueda-paginada-dialog';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';

/**
 * Diálogo de búsqueda de Profesionales (PPR) con paginación del lado del servidor.
 * Replica exactamente el legacy GWT: BusquedaActivity.java
 *
 * Legacy: SuraAsyncDataProvider con onChange(start, length) devuelve listaRegistros y rowCount
 *
 * Tabla: TPSC_PROFESIONALES
 * SQL: SELECT NMCONSECUTIVO, DNI, DSNOMBRE, DSMAIL FROM TPSC_PROFESIONALES
 *      WHERE FEBAJA IS NULL AND DNI LIKE '%value%' AND DSNOMBRE LIKE '%value%'
 *
 * Filtros del legacy:
 * - DNI: documento del profesional
 * - Nombre: nombre del profesional
 *
 * Usado para tipo de cita SEGUIMIENTO_INTEGRAL_CLIENTE (25)
 *
 * @author Ingeneo - Migración GWT a Angular
 */
/**
 * Origen de datos del buscador de profesionales.
 *
 * ORDEN_SERVICIO:  TPSC_ORDEN_SERVICIO_DETAL — solo profesionales con órdenes reales (Programar Servicios)
 * PROFESIONALES:   TPSC_PROFESIONALES — maestro completo con DSMAIL (Cita Dialog PPR)
 */
export enum BuscadorProfesionalOrigen {
  ORDEN_SERVICIO = 'ORDEN_SERVICIO',
  PROFESIONALES  = 'PROFESIONALES',
}

@Component({
  selector: 'app-busqueda-profesional-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    ProgressSpinnerModule,
    TooltipModule,
    DropdownModule,
    BusquedaPaginadorComponent
  ],
  providers: [
    { provide: ProfesionalRepository, useClass: HttpProfesionalRepository }
  ],
  styleUrls: ['../../shared/styles/grid-table.styles.scss'],
  template: `
    <p-dialog
      header="Búsqueda de Profesionales"
      [(visible)]="visible"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '54rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      [closable]="true"
      (onHide)="onCancelar()"
    >
      <!-- Sección Filtros de Búsqueda -->
      <div
        class="border dark:border-2 rounded p-4 mb-4 bg-gray-50 dark:bg-gray-800 dark:border-gray-700"
      >
        <div class="font-medium mb-3">Filtros de Búsqueda</div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <!-- DNI -->
          <div>
            <label class="block text-sm text-gray-600 mb-1">DNI</label>
            <input
              pInputText
              [(ngModel)]="filtro.dni"
              (input)="sanitizarCampo('dni')"
              class="w-full"
              maxlength="20"
              placeholder="Documento del profesional"
              (keyup.enter)="buscar()"
            />
          </div>

          <!-- Nombre -->
          <div>
            <label class="block text-sm text-gray-600 mb-1">Nombre</label>
            <input
              pInputText
              [(ngModel)]="filtro.nombre"
              (input)="sanitizarCampo('nombre')"
              class="w-full"
              maxlength="100"
              placeholder="Nombre del profesional"
              (keyup.enter)="buscar()"
            />
          </div>
        </div>

        <!-- Botones de filtro -->
        <div class="flex flex-wrap gap-2 mt-4">
          <button
            pButton
            icon="pi pi-trash"
            label="Limpiar"
            pTooltip="Limpiar los parámetros ingresados"
            class="p-button-sm p-button-secondary"
            (click)="limpiarFiltros()"
          ></button>
          <button
            pButton
            icon="pi pi-search"
            label="Buscar"
            pTooltip="Buscar con los parámetros seleccionados en el filtro"
            class="p-button-sm"
            (click)="buscar()"
            [disabled]="loading()"
          ></button>
        </div>
      </div>

      <!-- Mensaje de estado -->
      @if (mensaje()) {
        <div
          class="p-3 mb-3 rounded"
          [ngClass]="{
            'bg-yellow-100 text-yellow-800': mensajeTipo() === 'warning',
            'bg-blue-100 text-blue-800': mensajeTipo() === 'info',
            'bg-red-100 text-red-800': mensajeTipo() === 'error'
          }"
        >
          <i
            class="pi mr-2"
            [ngClass]="{
              'pi-exclamation-triangle': mensajeTipo() === 'warning',
              'pi-info-circle': mensajeTipo() === 'info',
              'pi-times-circle': mensajeTipo() === 'error'
            }"
          ></i>
          {{ mensaje() }}
        </div>
      }

      <!-- Listado de Profesionales -->
      <div class="border rounded" *ngIf="busquedaRealizada">
        <div
          class="font-medium text-gray-700 p-3 border-b bg-gray-50 text-sm flex justify-between items-center"
        >
          <span>Listado de Profesionales</span>
          @if (totalElements() > 0) {
            <span class="text-xs text-gray-500">
              Registros del {{ registroInicio() }} hasta el
              {{ registroFin() }} de {{ totalElements() }}
            </span>
          }
        </div>

        <div class="md:hidden space-y-3 p-3">
          @for (item of items(); track item.nmconsecutivo) {
            <div
              class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
            >
              <div class="mb-3 min-w-0">
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  Nombre
                </div>
                <div
                  class="font-semibold text-gray-800 dark:text-gray-100 truncate"
                  [attr.title]="tituloTabla(item.dsnombre)"
                >
                  {{ textoTabla(item.dsnombre) }}
                </div>
              </div>
              <div class="grid grid-cols-1 gap-2 text-sm mb-3">
                <div>
                  <span class="text-gray-500 dark:text-gray-400">DNI:</span>
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(item.dni)
                  }}</span>
                </div>
              </div>
              <p-button
                label="Seleccionar"
                icon="pi pi-check"
                size="small"
                styleClass="w-full min-h-[44px]"
                (onClick)="seleccionarItem(item)"
              />
            </div>
          }
        </div>

        <div class="hidden md:block">
          <div
            role="region"
            [attr.aria-label]="tableConfig().a11y.tableAriaLabel"
            class="sr-only"
          ></div>
          <p-table
            [resizableColumns]="true"
            columnResizeMode="expand"
            [value]="items()"
            [scrollable]="true"
            [responsiveLayout]="'scroll'"
            [scrollHeight]="tableConfig().responsive.scrollHeight || '300px'"
            [tableStyle]="{
              'min-width': tableConfig().responsive.minWidth || 'max-content'
            }"
            [dataKey]="
              (tableConfig().selection.dataKey || 'nmconsecutivo').toString()
            "
            [paginator]="false"
            [rows]="pageSize()"
            [loading]="loading()"
            [styleClass]="tableStyleClass()"
          >
            <ng-template pTemplate="header">
              <tr>
                <th pResizableColumn>
                  <span class="grid-header-text" title="DNI">DNI</span>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Nombre">Nombre</span>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Seleccionar"
                    >Seleccionar</span
                  >
                </th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-item>
              <tr>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(item.dni)"
                    >{{ textoTabla(item.dni) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(item.dsnombre)"
                    >{{ textoTabla(item.dsnombre) }}</span
                  >
                </td>
                <td>
                  <button
                    pButton
                    label="Seleccionar"
                    class="p-button-sm p-button-text"
                    (click)="seleccionarItem(item)"
                  ></button>
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="4" class="text-center text-gray-500 p-4">
                  No se encontraron profesionales con los criterios de búsqueda
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>

        <app-busqueda-paginador
          *ngIf="totalElements() > 0"
          [currentPage]="currentPage()"
          [totalPages]="totalPages()"
          [hasPrevious]="hasPrevious()"
          [hasNext]="hasNext()"
          [pageSizeOptions]="pageSizeOptions"
          [selectedPageSize]="selectedPageSize"
          (pageSizeChange)="onSelectedPageSizeChange($event)"
          (first)="irPrimeraPagina()"
          (previous)="loadPreviousPage()"
          (next)="loadNextPage()"
          (last)="irUltimaPagina()"
        ></app-busqueda-paginador>
      </div>

      @if (!busquedaRealizada) {
        <div class="text-center text-gray-500 p-4 border rounded">
          Ingrese los filtros y presione "Buscar"
        </div>
      }

      <!-- Botones footer -->
      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <p-button
            label="Cancelar"
            icon="pi pi-times"
            [outlined]="true"
            (onClick)="onCancelar()"
          ></p-button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class BusquedaProfesionalDialogComponent extends BaseBusquedaPaginadaDialog<Profesional> {
  /** Determina la tabla fuente: ORDEN_SERVICIO (default) o PROFESIONALES (cita PPR). */
  origen = input<BuscadorProfesionalOrigen>(BuscadorProfesionalOrigen.ORDEN_SERVICIO);

  // === Outputs ===
  onSelect = output<Profesional>();
  onClose = output<void>();

  // === Services ===
  private readonly profesionalRepository = inject(ProfesionalRepository);

  // === State ===
  visible = true;
  filtro: FiltroProfesional = {};
  seleccionado: Profesional | null = null;

  readonly totalElements = this.totalRegistros;
  readonly currentPage = this.paginaActual;
  readonly totalPages = this.totalPaginas;

  // Los botones Anterior/Siguiente dependen de la respuesta del servidor
  // (skill frontend-girop §17.7), por eso se sobreescriben los derivados de
  // la base (que los calcula desde la página actual).
  override hasPrevious = signal(false);
  override hasNext = signal(false);

  readonly tableConfig = computed(() =>
    busquedaProfesionalPreset.createConfig({
      selection: {
        mode: 'single',
        dataKey: 'nmconsecutivo',
        selected: this.seleccionado as unknown as Record<string, unknown> | null
      },
      style: {
        tableClassName: 'tabla-busqueda-profesional',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      state: {
        loading: this.loading(),
        emptyMessage:
          'No se encontraron profesionales con los criterios de busqueda'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: 'max-content',
        scrollHeight: '300px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de busqueda de profesionales',
        announceSelection: true,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly tableStyleClass = this.buildTableStyleClass(this.tableConfig);

  readonly tableSelectionMode = this.buildTableSelectionMode(this.tableConfig);

  /**
   * Busca profesionales - dispara la primera página.
   * Legacy: BusquedaActivity.siAccionFiltro() -> realizarBusqueda()
   */
  async buscar(): Promise<void> {
    this.sanitizarCampo('dni', true);
    this.sanitizarCampo('nombre', true);
    this.busquedaRealizada = true;
    // Búsqueda nueva: siempre página 1
    await this.buscarPagina(null, 'NEXT', 1);
  }

  /**
   * Sanitización XSS de texto libre (seguridad-girop §3). Mientras se teclea
   * conserva los espacios; antes de buscar también los recorta.
   */
  sanitizarCampo(campo: 'dni' | 'nombre', recortar = false): void {
    const valor = this.filtro[campo];
    if (valor === undefined) return;
    this.filtro[campo] = recortar
      ? sanitizeText(valor)
      : sanitizeTextPreserveSpaces(valor);
  }

  protected async ejecutarBusqueda(_start: number): Promise<void> {
    await this.buscarPagina(null, 'NEXT', 1);
  }

  /** Cursor devuelto por el backend para pedir la página siguiente */
  private nextCursorActual = signal<string | null>(null);

  /** Cursor devuelto por el backend para pedir la página anterior */
  private previousCursorActual = signal<string | null>(null);

  /**
   * Carga una página de profesionales por cursor (keyset, ver skill
   * acceso-datos-oracle §7). El desempate del cursor depende de origen()
   * (NMCONSECUTIVO para PROFESIONALES, DNI para ORDEN_SERVICIO) — un cursor
   * obtenido con un origen no es válido si origen() cambia entre llamadas.
   */
  private async buscarPagina(
    cursor: string | null,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST',
    pagina = 1
  ): Promise<void> {
    this.loading.set(true);
    this.mensaje.set('');

    try {
      const result = await this.profesionalRepository.buscarPaginado(
        this.filtro.dni?.trim() || undefined,
        this.filtro.nombre?.trim() || undefined,
        cursor,
        this.pageSize(),
        this.origen() ?? BuscadorProfesionalOrigen.ORDEN_SERVICIO,
        direction
      );

      this.items.set(result.content);
      this.totalElements.set(result.totalElements);
      this.nextCursorActual.set(result.nextCursor ?? null);
      this.previousCursorActual.set(result.previousCursor ?? null);

      this.hasNext.set(result.hasNext);
      this.hasPrevious.set(result.hasPrevious);

      // currentPage solo alimenta las etiquetas y cambia únicamente tras una
      // respuesta exitosa. Con LAST se calcula con el total del servidor.
      if (direction === 'LAST') {
        const size = this.pageSize();
        const total = result.totalElements ?? 0;
        this.currentPage.set(
          size > 0 && total > 0 ? Math.max(1, Math.ceil(total / size)) : 1
        );
      } else {
        this.currentPage.set(pagina);
      }

      if (result.totalElements === 0) {
        this.setMensaje('No se encontraron resultados', 'warning');
      }
    } catch (error) {
      console.error('Error buscando profesionales:', error);
      this.setMensaje(
        this.resolverMensajeErrorBusqueda(error, 'Error en la búsqueda'),
        'error'
      );
      this.items.set([]);
      this.totalElements.set(0);
    } finally {
      this.loading.set(false);
    }
  }

  // === Métodos de navegación de paginación ===

  override async irPrimeraPagina(): Promise<void> {
    if (this.loading()) {
      return;
    }
    // Ya en la primera página: no hay nada que recargar
    if (this.currentPage() === 1 && !this.hasPrevious()) {
      return;
    }
    await this.buscarPagina(null, 'NEXT', 1);
  }

  override async loadPreviousPage(): Promise<void> {
    if (this.loading() || !this.hasPrevious()) {
      return;
    }
    await this.buscarPagina(
      this.previousCursorActual(),
      'PREVIOUS',
      Math.max(1, this.currentPage() - 1)
    );
  }

  override async loadNextPage(): Promise<void> {
    if (this.loading() || !this.hasNext()) {
      return;
    }
    await this.buscarPagina(
      this.nextCursorActual(),
      'NEXT',
      this.currentPage() + 1
    );
  }

  override async irUltimaPagina(): Promise<void> {
    if (this.loading() || this.totalElements() === 0) {
      return;
    }
    await this.buscarPagina(null, 'LAST');
  }

  /**
   * Limpia los filtros y resultados.
   */
  limpiarFiltros(): void {
    this.filtro = {};
    this.items.set([]);
    this.clearMensajes();
    this.resetPaginacion();
    this.hasNext.set(false);
    this.hasPrevious.set(false);
    this.busquedaRealizada = false;
  }

  seleccionarItem(item: Profesional): void {
    this.onSelect.emit(item);
    this.visible = false;
  }

  /**
   * Cierra el diálogo sin selección.
   */
  onCancelar(): void {
    this.visible = false;
    this.onClose.emit();
  }

  /**
   * Establece un mensaje con tipo.
   */
  private setMensaje(texto: string, tipo: 'info' | 'warning' | 'error'): void {
    this.mensaje.set(texto);
    this.mensajeTipo.set(tipo);
  }
}
